import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtMXN, fmtDate, PAYMENT_PERIODS, PAYROLL_TYPES } from "@/lib/api";
import PageHeader, { StatusBadge } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { calcISR, periodDays } from "@/lib/isr";
import { Plus, DollarSign, Trash2, FileText, Zap, Camera, ScanFace, XCircle, Paperclip, Eye, CalendarDays, Wallet, Contact } from "lucide-react";
import { Modal, In, Sel } from "./Projects";
import ReceiptViewer from "@/components/ReceiptViewer";
import { toast } from "sonner";
import WorkdaysTab from "@/components/WorkdaysTab";
import EmployeeStatementModal from "@/components/EmployeeStatementModal";
import BatchPayrollModal from "@/components/BatchPayrollModal";
import EmployeeLoansTab from "@/components/EmployeeLoansTab";

const PAYROLL_STATUS = [
  { value: "borrador", label: "Borrador", color: "badge-mute" },
  { value: "pagado", label: "Pagado", color: "badge-success" },
  { value: "cancelado", label: "Cancelado", color: "badge-danger" },
];

const empE = { name: "", rfc: "", position: "", department: "", base_salary: 0, payment_period: "quincenal", payroll_type: "administrativa", project_id: "", hire_date: "", bank_account: "", face_photo_id: "", tax_regime: "", active: true };
const empP = { employee_id: "", period_start: "", period_end: "", gross: 0, deductions: 0, bonuses: 0, notes: "", extra_items: [], payment_method: "", paid_date: "" };

export default function Payroll() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = ["attendance", "workdays"].includes(searchParams.get("tab")) ? "workdays"
    : searchParams.get("tab") === "loans" ? "loans"
    : searchParams.get("tab") === "employees" ? "employees" : "entries";
  const [tab, setTab] = useState(initialTab);
  const [filterType, setFilterType] = useState("");
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [openEmp, setOpenEmp] = useState(false);
  const [openPay, setOpenPay] = useState(false);
  const [openBatch, setOpenBatch] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [empForm, setEmpForm] = useState(empE);
  const [payForm, setPayForm] = useState(empP);
  const [loanBalances, setLoanBalances] = useState({});
  const [statementEmp, setStatementEmp] = useState(null);

  const openCredential = async (e) => {
    try {
      const res = await api.get(`/employees/${e.id}/credential/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `credencial_${e.name.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Credencial generada");
    } catch { toast.error("No se pudo generar la credencial"); }
  };

  const canManage = ["admin", "contador", "auxiliar"].includes(user?.role);
  const canDeletePayroll = ["admin", "contador"].includes(user?.role);
  const canGenerate = ["admin", "contador", "auxiliar"].includes(user?.role);
  const isResidente = user?.role === "residente";
  const canCreateEmployee = ["admin", "contador", "auxiliar", "residente"].includes(user?.role);

  const load = async () => {
    const [e, en, p] = await Promise.all([api.get("/employees"), api.get("/payroll"), api.get("/projects")]);
    setEmployees(e.data);
    setEntries(en.data);
    setProjects(p.data);
    api.get("/employee-loans/balances").then(({ data }) => setLoanBalances(data || {})).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveEmp = async (ev) => {
    ev.preventDefault();
    const payload = { ...empForm, base_salary: Number(empForm.base_salary) };
    Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
    payload.tax_regime = empForm.tax_regime || "";
    if (editEmp) await api.patch(`/employees/${editEmp}`, payload);
    else await api.post("/employees", payload);
    setOpenEmp(false);
    setEmpForm(empE);
    setEditEmp(null);
    load();
  };

  const startEditEmp = (e) => { setEditEmp(e.id); setEmpForm({ ...empE, ...e }); setOpenEmp(true); };

  const asimIsr = () => {
    const emp = employees.find((e) => e.id === payForm.employee_id);
    if (!emp || emp.tax_regime !== "asimilado" || !payForm.period_start || !payForm.period_end) return 0;
    const base = Number(payForm.gross || 0) + Number(payForm.bonuses || 0)
      + (payForm.extra_items || []).reduce((a, i) => a + (i.type === "percepcion" ? Number(i.amount) || 0 : 0), 0);
    return calcISR(base, periodDays(payForm.period_start, payForm.period_end));
  };

  const removeEmp = async (id) => { if (!window.confirm("¿Eliminar empleado?")) return; await api.delete(`/employees/${id}`); load(); };

  useEffect(() => {
    if (openPay) {
      api.get("/employee-loans/balances").then(({ data }) => setLoanBalances(data || {})).catch(() => {});
    }
  }, [openPay]);

  const savePay = async (ev) => {
    ev.preventDefault();
    const { _summary, apply_loan, ...rest } = payForm;
    const lb = loanBalances[payForm.employee_id] || {};
    const loanBal = lb.balance || 0;
    const loanDeduct = loanBal > 0 && apply_loan !== false ? Math.min(lb.suggested ?? loanBal, loanBal) : 0;
    const extraItems = (rest.extra_items || [])
      .map((i) => ({ label: (i.label || "").trim(), type: i.type, amount: Number(i.amount) }))
      .filter((i) => i.label && i.amount > 0);
    const payload = {
      ...rest,
      gross: Number(rest.gross),
      deductions: Number(rest.deductions || 0) + loanDeduct,
      bonuses: Number(rest.bonuses || 0),
      loan_deduction: loanDeduct,
      extra_items: extraItems,
      payment_method: rest.payment_method || null,
      paid_date: rest.paid_date || null,
    };
    await api.post("/payroll", payload);
    setOpenPay(false);
    setPayForm(empP);
    load();
  };

  const markPaid = async (id) => { await api.post(`/payroll/${id}/pay`); load(); };
  const removePay = async (id) => { if (!window.confirm("¿Eliminar registro?")) return; await api.delete(`/payroll/${id}`); load(); };

  const [proofFor, setProofFor] = useState(null);
  const [viewProof, setViewProof] = useState(null);
  const proofRef = useRef(null);
  const pickProof = (id) => { setProofFor(id); proofRef.current?.click(); };
  const uploadProof = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file || !proofFor) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/payroll/${proofFor}/payment-proof`, { file_id: up.data.id });
      toast.success("Comprobante de pago adjuntado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir comprobante"); }
    setProofFor(null);
  };

  return (
    <div data-testid="payroll-page">
      <PageHeader
        subtitle="Empleados y pagos"
        title="Nómina"
        actions={
          <div className="flex flex-wrap gap-2">
            {canCreateEmployee && (
              <button data-testid="new-employee-btn" onClick={() => { setEditEmp(null); setEmpForm({ ...empE, payroll_type: isResidente ? "obra" : empE.payroll_type }); setOpenEmp(true); }} className="btn-ghost px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
                <Plus className="w-4 h-4" /> Empleado
              </button>
            )}
            {canGenerate && (
              <button data-testid="batch-payroll-btn" onClick={() => setOpenBatch(true)} className="btn-ghost px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold" style={{ borderColor: "var(--magenta)", color: "var(--magenta)" }}>
                <Zap className="w-4 h-4" /> Lista de raya
              </button>
            )}
            {canGenerate && (
              <button data-testid="new-payroll-btn" onClick={() => { setPayForm({ ...empP, employee_id: employees[0]?.id || "" }); setOpenPay(true); }} className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
                <DollarSign className="w-4 h-4" /> Generar pago
              </button>
            )}
          </div>
        }
      />
      <div className="px-6 lg:px-8 py-6">
        <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:gap-6 sm:items-end sm:justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="flex gap-4 sm:gap-6 overflow-x-auto pb-1">
            <Tab active={tab === "entries"} onClick={() => setTab("entries")} testId="tab-entries">Pagos ({entries.filter(e => !filterType || e.payroll_type === filterType).length})</Tab>
            <Tab active={tab === "employees"} onClick={() => setTab("employees")} testId="tab-employees">Empleados ({employees.filter(e => !filterType || e.payroll_type === filterType).length})</Tab>
            <Tab active={tab === "workdays"} onClick={() => setTab("workdays")} testId="tab-workdays"><CalendarDays className="w-3.5 h-3.5 inline mr-1" />Días trabajados</Tab>
            <Tab active={tab === "loans"} onClick={() => setTab("loans")} testId="tab-loans">Préstamos y cargos</Tab>
          </div>
          {tab !== "workdays" && tab !== "loans" && (
            <div className="flex gap-1 pb-2 overflow-x-auto">
              {[{v:"",l:"Todos"},{v:"obra",l:"Obra"},{v:"administrativa",l:"Admin"}].filter(o => !isResidente || o.v !== "administrativa").map(o => (
                <button
                  key={o.v}
                  data-testid={`filter-type-${o.v || 'all'}`}
                  onClick={() => setFilterType(o.v)}
                  className={`px-3 py-1 text-[10px] uppercase tracking-widest font-semibold rounded-sm transition-colors ${filterType === o.v ? "" : "hover:bg-white/5"}`}
                  style={filterType === o.v
                    ? { background: "var(--cyan)", color: "#0c1424", boxShadow: "0 0 12px var(--cyan-glow)" }
                    : { color: "var(--text-dim)", border: "1px solid var(--border-2)" }}
                >
                  {o.l}
                </button>
              ))}
            </div>
          )}
        </div>

        {tab === "entries" && (
          <div className="panel overflow-hidden">
            <input ref={proofRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={uploadProof} data-testid="proof-file-input" />
            {viewProof && <ReceiptViewer fileId={viewProof} title="Comprobante de pago" onClose={() => setViewProof(null)} />}
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]" data-testid="payroll-entries-table">
              <thead className="panel-header">
                <tr className="text-left">
                  <Th>Empleado</Th><Th>Tipo</Th><Th>Período</Th><Th right>Bruto</Th><Th right>Bonos</Th><Th right>Deducc.</Th>
                  <Th right>Neto</Th><Th>Estado</Th><Th right>Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {entries.filter(e => !filterType || e.payroll_type === filterType).length === 0 && (
                  <tr><td colSpan={9} className="text-center py-12 text-slate-500">Sin pagos generados.</td></tr>
                )}
                {entries.filter(e => !filterType || e.payroll_type === filterType).map((e) => (
                  <tr key={e.id} data-testid={`payroll-row-${e.id}`} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                    <Td className="font-semibold text-slate-100">{e.employee_name}</Td>
                    <Td>
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm uppercase tracking-widest ${e.payroll_type === "obra" ? "badge-magenta" : "badge-info"}`}>
                        {e.payroll_type === "obra" ? "OBRA" : "ADMIN"}
                      </span>
                      {(e.expense_id || e.admin_expense_id) && <div className="label-mono text-[9px] mt-1" style={{ color: "var(--cyan)" }} title={e.expense_id ? "Vinculado a gastos de obra" : "Vinculado a gastos administrativos"}>↳ gasto vinculado</div>}
                    </Td>
                    <Td className="text-xs text-slate-300">{fmtDate(e.period_start)} → {fmtDate(e.period_end)}</Td>
                    <Td right className="font-mono text-slate-100">{fmtMXN(e.gross)}</Td>
                    <Td right className="font-mono" style={{ color: "var(--green)" }}>+{fmtMXN(e.bonuses)}</Td>
                    <Td right className="font-mono" style={{ color: "var(--red)" }}>-{fmtMXN(e.deductions)}</Td>
                    <Td right className="font-mono font-bold" style={{ color: "var(--cyan)" }}>{fmtMXN(e.net)}</Td>
                    <Td><StatusBadge items={PAYROLL_STATUS} value={e.status} /></Td>
                    <Td right>
                      <a href={`/payroll/${e.id}/receipt`} target="_blank" rel="noreferrer" data-testid={`receipt-${e.id}`} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline mr-2" style={{ color: "var(--cyan)" }} title="Recibo de pago">
                        <FileText className="w-3.5 h-3.5" /> RECIBO
                      </a>
                      {e.signature && (
                        <span data-testid={`signed-${e.id}`} className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-bold mr-2" style={{ background: "rgba(34,197,94,0.12)", color: "var(--green)", border: "1px solid rgba(34,197,94,0.4)" }} title={`Firmado ${e.signed_at?.slice(0, 10) || ""}`}>
                          ✓ FIRMADO
                        </span>
                      )}
                      {e.payment_proof_id ? (
                        <button data-testid={`view-proof-${e.id}`} onClick={() => setViewProof(e.payment_proof_id)} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline mr-2" style={{ color: "var(--green)" }} title={`Comprobante de pago${e.payment_proof_by ? " · " + e.payment_proof_by : ""}`}>
                          <Eye className="w-3.5 h-3.5" /> COMP.
                        </button>
                      ) : (
                        canManage && (
                          <button data-testid={`attach-proof-${e.id}`} onClick={() => pickProof(e.id)} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline mr-2 text-slate-400" title="Subir comprobante de pago">
                            <Paperclip className="w-3.5 h-3.5" /> COMP.
                          </button>
                        )
                      )}
                      {canManage && e.status === "borrador" && (
                        <button data-testid={`pay-payroll-${e.id}`} onClick={() => markPaid(e.id)} className="text-xs font-semibold hover:underline mr-2" style={{ color: "var(--green)" }}>PAGAR</button>
                      )}
                      {canDeletePayroll && (
                        <button data-testid={`delete-payroll-${e.id}`} onClick={() => removePay(e.id)} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {tab === "workdays" && (
          <WorkdaysTab employees={employees} projects={projects}
            canWrite={["admin", "contador", "auxiliar", "residente"].includes(user?.role)}
            canPay={["admin", "contador", "auxiliar"].includes(user?.role)}
            payrolls={entries}
            onGeneratePay={(prefill) => { setPayForm({ ...empP, ...prefill }); setOpenPay(true); }} />
        )}

        {tab === "loans" && (
          <EmployeeLoansTab employees={employees} />
        )}

        {tab === "employees" && (
          <div className="panel overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]" data-testid="employees-table">
              <thead className="panel-header">
                <tr className="text-left">
                  <Th>Nombre</Th><Th>Tipo</Th><Th>Puesto</Th><Th>Departamento</Th>
                  <Th right>Salario base</Th><Th>Período</Th><Th>Activo</Th><Th right>Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {employees.filter(e => !filterType || e.payroll_type === filterType).length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-slate-500">Sin empleados.</td></tr>
                )}
                {employees.filter(e => !filterType || e.payroll_type === filterType).map((e) => (
                  <tr key={e.id} data-testid={`employee-row-${e.id}`} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                    <Td className="font-semibold text-slate-100">
                      {e.name}
                      {e.rfc && <div className="font-mono text-[10px] text-slate-500">{e.rfc}</div>}
                    </Td>
                    <Td>
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm uppercase tracking-widest ${e.payroll_type === "obra" ? "badge-magenta" : "badge-info"}`}>
                        {e.payroll_type === "obra" ? "OBRA" : "ADMIN"}
                      </span>
                      {e.payroll_type === "obra" && e.project_id && (
                        <div className="label-mono text-[9px] mt-1" style={{ color: "var(--cyan)" }}>
                          {projects.find(p => p.id === e.project_id)?.code || "—"}
                        </div>
                      )}
                    </Td>
                    <Td className="text-slate-300">{e.position}</Td>
                    <Td className="text-slate-300">{e.department || "—"}</Td>
                    <Td right className="font-mono text-slate-100">{fmtMXN(e.base_salary)}</Td>
                    <Td className="capitalize text-slate-300">{e.payment_period}</Td>
                    <Td>{e.active ? <span className="text-xs font-semibold" style={{ color: "var(--green)" }}>SÍ</span> : <span className="text-slate-500 text-xs">NO</span>}</Td>
                    <Td right>
                      <button data-testid={`statement-employee-${e.id}`} onClick={() => setStatementEmp(e)} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline mr-2" style={{ color: "var(--green)" }} title="Estado de cuenta del trabajador">
                        <Wallet className="w-3.5 h-3.5" /> EDO. CTA
                      </button>
                      <button data-testid={`credential-employee-${e.id}`} onClick={() => openCredential(e)} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline mr-2" style={{ color: "#a78bfa" }} title="Generar credencial de identificación (PDF)">
                        <Contact className="w-3.5 h-3.5" /> CREDENCIAL
                      </button>
                      {!isResidente && (
                        <button data-testid={`edit-employee-${e.id}`} onClick={() => startEditEmp(e)} className="text-xs font-semibold hover:underline mr-2" style={{ color: "var(--cyan)" }}>EDITAR</button>
                      )}
                      {user?.role === "admin" && (
                        <button data-testid={`delete-employee-${e.id}`} onClick={() => removeEmp(e.id)} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {openEmp && (
        <Modal onClose={() => setOpenEmp(false)} title={editEmp ? "Editar empleado" : "Nuevo empleado"}>
          <form onSubmit={saveEmp} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <In label="Nombre" testId="emp-name" value={empForm.name} onChange={(v) => setEmpForm({ ...empForm, name: v })} />
              <In label="RFC" testId="emp-rfc" value={empForm.rfc} onChange={(v) => setEmpForm({ ...empForm, rfc: v })} required={false} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ComboField
                label="Puesto"
                testId="emp-position"
                value={empForm.position}
                onChange={(v) => setEmpForm({ ...empForm, position: v })}
                options={[...new Set(employees.map((e) => (e.position || "").trim()).filter(Boolean))].sort()}
                addLabel="➕ Agregar nuevo puesto…"
                placeholder="Ej. Albañil, Oficial, Velador…"
                required
              />
              <ComboField
                label="Departamento"
                testId="emp-dept"
                value={empForm.department}
                onChange={(v) => setEmpForm({ ...empForm, department: v })}
                options={[...new Set(employees.map((e) => (e.department || "").trim()).filter(Boolean))].sort()}
                addLabel="➕ Agregar nuevo departamento…"
                placeholder="Ej. Obras, Administración…"
              />
            </div>
            {!isResidente && (
              <div>
                <label className="label-mono block mb-1.5">Tipo de nómina</label>
                <div className="grid grid-cols-2 gap-2">
                  {PAYROLL_TYPES.map(t => (
                    <button
                      key={t.value}
                      type="button"
                      data-testid={`emp-type-${t.value}`}
                      onClick={() => setEmpForm({ ...empForm, payroll_type: t.value, project_id: t.value === "administrativa" ? "" : empForm.project_id })}
                      className="px-3 py-2 text-xs font-semibold rounded-sm transition-colors text-left"
                      style={empForm.payroll_type === t.value
                        ? { background: t.value === "obra" ? "rgba(244,114,182,0.15)" : "rgba(34,211,238,0.15)", border: `1px solid ${t.value === "obra" ? "var(--magenta)" : "var(--cyan)"}`, color: t.value === "obra" ? "#f9a8d4" : "#67e8f9" }
                        : { background: "var(--bg-2)", border: "1px solid var(--border-2)", color: "var(--text-dim)" }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <In label="Salario base MXN" testId="emp-salary" type="number" value={empForm.base_salary} onChange={(v) => setEmpForm({ ...empForm, base_salary: v })} />
              <Sel label="Período" testId="emp-period" value={empForm.payment_period} onChange={(v) => setEmpForm({ ...empForm, payment_period: v })} options={PAYMENT_PERIODS} />
              {empForm.payroll_type === "obra" ? (
                <Sel label="Proyecto" testId="emp-project" value={empForm.project_id || ""} onChange={(v) => setEmpForm({ ...empForm, project_id: v })}
                  options={[{ value: "", label: "— Selecciona —" }, ...(isResidente ? projects.filter(p => p.residente_id === user.id) : projects).map((p) => ({ value: p.id, label: `${p.code} - ${p.name}` }))]} />
              ) : (
                <div>
                  <label className="label-mono block mb-1.5">Proyecto</label>
                  <div className="input-cyber px-3 py-2 text-xs text-slate-500">N/A</div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label="Fecha de ingreso" testId="emp-hire" type="date" value={empForm.hire_date || ""} onChange={(v) => setEmpForm({ ...empForm, hire_date: v })} required={false} />
              <In label="Cuenta bancaria" testId="emp-bank" value={empForm.bank_account} onChange={(v) => setEmpForm({ ...empForm, bank_account: v })} required={false} />
              <div>
                <label className="label-mono block mb-1.5">Régimen fiscal</label>
                <select data-testid="emp-tax-regime" value={empForm.tax_regime || ""} onChange={(ev) => setEmpForm({ ...empForm, tax_regime: ev.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
                  <option value="">Nómina normal</option>
                  <option value="asimilado">Honorarios asimilados (retener ISR)</option>
                </select>
              </div>
            </div>
            {empForm.payroll_type === "obra" && (
              <div className="text-xs text-slate-400 px-3 py-2.5 rounded-sm" style={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }}>
                <span className="label-mono" style={{ color: "var(--magenta)" }}>// AUTOMATIZACIÓN</span> Cada pago de nómina a este empleado generará automáticamente un gasto en el proyecto seleccionado (categoría "Mano de obra").
              </div>
            )}

            {/* Face reference photo for AI verification */}
            <EmployeeFacePhoto empForm={empForm} setEmpForm={setEmpForm} />

            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenEmp(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" data-testid="save-employee-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Guardar</button>
            </div>
          </form>
        </Modal>
      )}

      {openPay && (
        <Modal onClose={() => setOpenPay(false)} title="Generar pago de nómina">
          <form onSubmit={savePay} className="space-y-4">
            <Sel label="Empleado" testId="pay-employee" value={payForm.employee_id} onChange={(v) => setPayForm({ ...payForm, employee_id: v, _summary: null })}
              options={employees.map((e) => ({ value: e.id, label: `${e.name} — ${e.position} ${e.payroll_type === "obra" ? "(OBRA)" : ""}` }))} />
            <div className="grid grid-cols-2 gap-3">
              <In label="Inicio período" testId="pay-start" type="date" value={payForm.period_start} onChange={(v) => setPayForm({ ...payForm, period_start: v, _summary: null })} />
              <In label="Fin período" testId="pay-end" type="date" value={payForm.period_end} onChange={(v) => setPayForm({ ...payForm, period_end: v, _summary: null })} />
            </div>

            {(() => {
              const selectedEmp = employees.find(e => e.id === payForm.employee_id);
              if (!selectedEmp) return null;
              return (
                <div className="rounded-sm p-3" style={{ background: "rgba(244,114,182,0.06)", border: "1px solid rgba(244,114,182,0.3)" }}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="label-mono" style={{ color: "var(--magenta)" }}>// CÁLCULO AUTOMÁTICO</div>
                      <div className="text-xs text-slate-300 mt-0.5">
                        Calcula el bruto desde los días marcados en el calendario de este período.
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid="calc-from-workdays-btn"
                      disabled={!payForm.employee_id || !payForm.period_start || !payForm.period_end}
                      onClick={async () => {
                        try {
                          const { data } = await api.get("/workdays/summary", {
                            params: { employee_id: payForm.employee_id, start: payForm.period_start, end: payForm.period_end },
                          });
                          setPayForm({ ...payForm, gross: data.gross, _summary: data });
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Error al calcular");
                        }
                      }}
                      className="btn-primary px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50"
                    >
                      Calcular desde días trabajados
                    </button>
                  </div>
                  {payForm._summary && (
                    <div className="grid grid-cols-5 gap-2 mt-3 text-xs" data-testid="workdays-calc-summary">
                      <Mini label="COMPLETOS" value={payForm._summary.completos} />
                      <Mini label="MEDIOS" value={payForm._summary.medios} />
                      <Mini label="EXTRAS" value={payForm._summary.extras} />
                      <Mini label="FALTAS" value={payForm._summary.faltas} />
                      <Mini label="VALOR DÍA" value={fmtMXN(payForm._summary.day_value)} />
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="grid grid-cols-3 gap-3">
              <In label="Sueldo bruto" testId="pay-gross" type="number" value={payForm.gross} onChange={(v) => setPayForm({ ...payForm, gross: v })} />
              <In label="Bonos" testId="pay-bonus" type="number" value={payForm.bonuses} onChange={(v) => setPayForm({ ...payForm, bonuses: v })} required={false} />
              <In label="Deducciones" testId="pay-ded" type="number" value={payForm.deductions} onChange={(v) => setPayForm({ ...payForm, deductions: v })} required={false} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-mono block mb-1.5">Método de pago</label>
                <select data-testid="pay-method" value={payForm.payment_method} onChange={(ev) => setPayForm({ ...payForm, payment_method: ev.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
                  <option value="">— Sin especificar —</option>
                  <option value="deposito">Depósito</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="efectivo">Efectivo</option>
                </select>
              </div>
              <In label="Día en que se pagó" testId="pay-paid-date" type="date" value={payForm.paid_date} onChange={(v) => setPayForm({ ...payForm, paid_date: v })} required={false} />
            </div>
            {(() => {
              const lb = loanBalances[payForm.employee_id] || {};
              const loanBal = lb.balance || 0;
              if (loanBal <= 0) return null;
              const sug = Math.min(lb.suggested ?? loanBal, loanBal);
              const apply = payForm.apply_loan !== false;
              return (
                <div className="rounded-sm p-3 flex items-center gap-3 flex-wrap" data-testid="loan-deduction-banner"
                  style={{ background: "rgba(251,191,36,0.07)", border: "1px solid rgba(251,191,36,0.35)" }}>
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-200 flex-1 min-w-[220px]">
                    <input type="checkbox" checked={apply} data-testid="loan-apply-checkbox"
                      onChange={(e) => setPayForm({ ...payForm, apply_loan: e.target.checked })} className="accent-cyan-400" />
                    <span>💰 Tiene <b style={{ color: "#fbbf24" }}>{fmtMXN(loanBal)}</b> pendiente de préstamos/cargos — descontar {sug < loanBal ? <>parcialidad de <b style={{ color: "#fbbf24" }}>{fmtMXN(sug)}</b></> : "en este pago"}</span>
                  </label>
                  {apply && <span className="font-mono text-xs" style={{ color: "#fbbf24" }}>-{fmtMXN(sug)}</span>}
                </div>
              );
            })()}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="label-mono">Conceptos extra (horas extra, faltas, uniformes…)</span>
                <button type="button" data-testid="add-extra-item-btn"
                  onClick={() => setPayForm({ ...payForm, extra_items: [...(payForm.extra_items || []), { label: "", type: "percepcion", amount: "" }] })}
                  className="btn-ghost px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest" style={{ color: "var(--cyan)" }}>
                  ＋ Agregar concepto
                </button>
              </div>
              {(payForm.extra_items || []).map((it, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <input placeholder="Concepto (ej. Horas extra)" data-testid={`extra-label-${idx}`} value={it.label}
                    onChange={(ev) => setPayForm({ ...payForm, extra_items: payForm.extra_items.map((x, i) => i === idx ? { ...x, label: ev.target.value } : x) })}
                    className="input-cyber px-2.5 py-1.5 text-xs flex-1" />
                  <select data-testid={`extra-type-${idx}`} value={it.type}
                    onChange={(ev) => setPayForm({ ...payForm, extra_items: payForm.extra_items.map((x, i) => i === idx ? { ...x, type: ev.target.value } : x) })}
                    className="input-cyber px-2 py-1.5 text-xs w-32">
                    <option value="percepcion">＋ Percepción</option>
                    <option value="deduccion">－ Deducción</option>
                  </select>
                  <input type="number" min="0" step="0.01" placeholder="$" data-testid={`extra-amount-${idx}`} value={it.amount}
                    onChange={(ev) => setPayForm({ ...payForm, extra_items: payForm.extra_items.map((x, i) => i === idx ? { ...x, amount: ev.target.value } : x) })}
                    className="input-cyber px-2.5 py-1.5 text-xs w-24 text-right" />
                  <button type="button" data-testid={`extra-remove-${idx}`}
                    onClick={() => setPayForm({ ...payForm, extra_items: payForm.extra_items.filter((_, i) => i !== idx) })}
                    className="btn-ghost p-1.5 rounded-sm" style={{ color: "var(--red)" }}>
                    <XCircle className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>

            <div className="px-4 py-3 rounded-sm flex items-center justify-between" style={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }}>
              <span className="label-mono">Neto a pagar</span>
              <span className="metric-value text-2xl tabular-nums" style={{ color: "var(--cyan)", textShadow: "0 0 12px var(--cyan-glow)" }}>
                {fmtMXN(Number(payForm.gross || 0) + Number(payForm.bonuses || 0) - Number(payForm.deductions || 0)
                  + (payForm.extra_items || []).reduce((a, i) => a + (i.type === "percepcion" ? 1 : -1) * (Number(i.amount) || 0), 0)
                  - ((loanBalances[payForm.employee_id] || 0) > 0 && payForm.apply_loan !== false ? loanBalances[payForm.employee_id] : 0)
                  - asimIsr())}
              </span>
            </div>
            {asimIsr() > 0 && (
              <div className="px-3 py-2 rounded-sm text-xs font-mono flex justify-between" data-testid="isr-preview"
                style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.35)" }}>
                <span style={{ color: "#fbbf24" }}>Retención ISR (honorarios asimilados)</span>
                <b style={{ color: "#fbbf24" }}>- {fmtMXN(asimIsr())}</b>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenPay(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" data-testid="save-payroll-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Generar</button>
            </div>
          </form>
        </Modal>
      )}
      {statementEmp && <EmployeeStatementModal employee={statementEmp} onClose={() => setStatementEmp(null)} />}
      {openBatch && (
        <BatchPayrollModal
          projects={projects}
          employees={employees}
          onClose={() => setOpenBatch(false)}
          onDone={() => { setOpenBatch(false); load(); }}
        />
      )}
    </div>
  );
}

const Mini = ({ label, value }) => (
  <div className="px-2.5 py-2 rounded-sm" style={{ background: "var(--panel)", border: "1px solid var(--border)" }}>
    <div className="label-mono text-[9px]">{label}</div>
    <div className="font-mono font-bold text-sm text-slate-100 mt-0.5">{value}</div>
  </div>
);

const Tab = ({ children, active, onClick, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className="pb-3 text-sm font-semibold transition-colors -mb-px"
    style={active ? { color: "var(--cyan)", borderBottom: "2px solid var(--cyan)", textShadow: "0 0 10px var(--cyan-glow)" } : { color: "var(--text-dim)", borderBottom: "2px solid transparent" }}>
    {children}
  </button>
);
const Th = ({ children, right }) => <th className={`label-mono px-4 py-3 ${right ? "text-right" : ""}`}>{children}</th>;
const Td = ({ children, right, className = "" }) => <td className={`px-4 py-3 align-top ${right ? "text-right" : ""} ${className}`}>{children}</td>;


function EmployeeFacePhoto({ empForm, setEmpForm }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const [validation, setValidation] = useState(null); // { valid, has_face, quality, reason }
  const onChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setValidation(null);
    try {
      // Step 1: validate with AI BEFORE saving
      const vfd = new FormData();
      vfd.append("file", file);
      const { data: val } = await api.post("/employees/validate-face-photo", vfd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setValidation(val);
      if (!val.valid) {
        // Don't upload an invalid photo
        return;
      }
      // Step 2: upload only if valid
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEmpForm({ ...empForm, face_photo_id: data.id });
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Error al validar foto";
      setValidation({ valid: false, has_face: false, quality: "bad", reason: detail });
    } finally {
      setUploading(false);
    }
  };
  const has = !!empForm.face_photo_id;
  const src = has ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${empForm.face_photo_id}` : null;
  return (
    <div className="panel p-3 space-y-2" style={{ background: "rgba(8,12,24,0.6)" }}>
      <div className="flex items-center gap-2">
        <ScanFace className="w-4 h-4" style={{ color: "var(--magenta)" }} />
        <span className="label-mono" style={{ color: "var(--magenta)" }}>// FOTO DEL TRABAJADOR · VALIDADA CON IA</span>
      </div>
      <p className="text-[11px] text-slate-500">Sube una foto clara del rostro del trabajador. Se usará para su credencial de identificación y para verificar identidad.</p>
      <div className="flex items-center gap-3">
        {has && <img src={src} alt="rostro" className="w-16 h-16 rounded-sm object-cover" style={{ border: "1px solid var(--green)" }} />}
        {!has && (
          <div className="w-16 h-16 rounded-sm flex items-center justify-center" style={{ background: "var(--bg-2)", border: "1px dashed var(--border-2)" }}>
            <Camera className="w-5 h-5 text-slate-500" />
          </div>
        )}
        <input ref={ref} type="file" accept="image/*" capture="user" onChange={onChange} className="hidden" data-testid="emp-face-input" />
        <button type="button" data-testid="emp-face-btn" onClick={() => ref.current?.click()} disabled={uploading}
          className="btn-ghost px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5 disabled:opacity-40">
          <Camera className="w-3.5 h-3.5" /> {uploading ? "Validando…" : (has ? "Cambiar" : "Subir foto")}
        </button>
        {has && (
          <button type="button" onClick={() => { setEmpForm({ ...empForm, face_photo_id: "" }); setValidation(null); }}
            className="text-xs label-mono hover:underline" style={{ color: "var(--red)" }}>QUITAR</button>
        )}
      </div>
      {validation && (
        <div data-testid="emp-face-validation" className="text-[11px] px-2 py-1.5 rounded-sm flex items-start gap-1.5"
          style={{
            background: validation.valid ? "rgba(34,197,94,0.10)" : "rgba(220,38,38,0.10)",
            border: `1px solid ${validation.valid ? "var(--green)" : "var(--red)"}`,
            color: validation.valid ? "#86efac" : "#fca5a5",
          }}>
          <span>{validation.valid ? "✓" : "✗"}</span>
          <span>{validation.valid ? "Foto apta como referencia facial." : `Rechazada: ${validation.reason}`}</span>
        </div>
      )}
    </div>
  );
}

function ComboField({ label, testId, value, onChange, options, addLabel, placeholder, required = false }) {
  const knownValue = !value || options.includes(value);
  const [adding, setAdding] = useState(!knownValue);
  useEffect(() => { setAdding(value ? !options.includes(value) : false); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <label className="label-mono block mb-1.5">{label}</label>
      {adding ? (
        <div className="flex gap-1.5">
          <input
            data-testid={`${testId}-input`}
            required={required}
            autoFocus
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="input-cyber px-3 py-2 text-sm w-full"
          />
          {options.length > 0 && (
            <button
              type="button"
              data-testid={`${testId}-back-to-select`}
              onClick={() => { onChange(""); setAdding(false); }}
              className="btn-ghost px-2.5 rounded-sm text-xs shrink-0"
              title="Elegir de la lista"
            >
              ↩
            </button>
          )}
        </div>
      ) : (
        <select
          data-testid={`${testId}-select`}
          required={required}
          value={value}
          onChange={(e) => {
            if (e.target.value === "__add__") { onChange(""); setAdding(true); }
            else onChange(e.target.value);
          }}
          className="input-cyber px-3 py-2 text-sm w-full"
        >
          <option value="">— Selecciona —</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
          <option value="__add__">{addLabel}</option>
        </select>
      )}
    </div>
  );
}

